import { gql } from "@apollo/client";

/**
 * Third-party Integration API — MBO oversight surface (OS-2551).
 *
 * Standard Hasura/JWT back-office operations under the `osorno_administrator` role,
 * mirroring the OEE module-license enable/disable flow. Every state change and its
 * audit row are written in a SINGLE mutation document so they share one transaction
 * (contract §10.3, Decision R3); the toggle-off auto-revoke cascade runs inside that
 * same transaction via the DB trigger, so the UI never loops over keys.
 */

// List across ALL Corporations: name, enable-gate state, and an aggregate of the
// Corporation's *active* keys (not revoked, not expired) — count + most-recent usage.
export const getApiAccessCorporations = gql`
    query GetApiAccessCorporations($now: timestamp!) {
        corporate(order_by: { created_at: desc }) {
            corporate_id
            name
            third_party_api_corporate_config {
                corporate_id
                is_enabled
            }
            third_party_api_keys_aggregate(where: { revoked_at: { _is_null: true }, expires_at: { _gt: $now } }) {
                aggregate {
                    count
                    max {
                        last_used_at
                    }
                }
            }
        }
    }
`;

// Per-key drill-down for one Corporation. Only metadata + key_prefix are selectable —
// there is no raw-key column to return (contract §5.2).
export const getApiAccessKeys = gql`
    query GetApiAccessKeys($corporate_id: Int!) {
        third_party_api_key(where: { corporate_id: { _eq: $corporate_id } }, order_by: { created_at: desc }) {
            third_party_api_key_id
            corporate_id
            key_prefix
            label
            created_at
            expires_at
            last_used_at
            revoked_at
        }
    }
`;

// Enable ON: upsert the config row (a Corporation may have no row yet) + audit, one txn.
export const enableApiAccess = gql`
    mutation EnableApiAccess($corporate_id: Int!, $changed_by_user_id: Int) {
        insert_third_party_api_corporate_config_one(
            object: { corporate_id: $corporate_id, is_enabled: true }
            on_conflict: { constraint: third_party_api_corporate_config_pkey, update_columns: [is_enabled] }
        ) {
            corporate_id
            is_enabled
        }
        insert_third_party_api_audit_log_one(
            object: {
                corporate_id: $corporate_id
                action: "api_access_enabled"
                new_value: "true"
                changed_by_user_id: $changed_by_user_id
            }
        ) {
            id
        }
    }
`;

// Disable OFF: update is_enabled -> false + audit, one txn. The AFTER-UPDATE trigger
// cascades revoked_at = now() to every active key of this Corporation in the same txn.
export const disableApiAccess = gql`
    mutation DisableApiAccess($corporate_id: Int!, $changed_by_user_id: Int) {
        update_third_party_api_corporate_config(
            where: { corporate_id: { _eq: $corporate_id }, is_enabled: { _eq: true } }
            _set: { is_enabled: false }
        ) {
            affected_rows
        }
        insert_third_party_api_audit_log_one(
            object: {
                corporate_id: $corporate_id
                action: "api_access_disabled"
                new_value: "false"
                changed_by_user_id: $changed_by_user_id
            }
        ) {
            id
        }
    }
`;

// Per-key revoke (incident response): set revoked_at + audit (key id in new_value), one txn.
export const revokeApiKey = gql`
    mutation RevokeApiKey(
        $third_party_api_key_id: bigint!
        $key_id_text: String!
        $corporate_id: Int!
        $revoked_at: timestamp!
        $changed_by_user_id: Int
    ) {
        update_third_party_api_key(
            where: { third_party_api_key_id: { _eq: $third_party_api_key_id }, revoked_at: { _is_null: true } }
            _set: { revoked_at: $revoked_at }
        ) {
            affected_rows
        }
        insert_third_party_api_audit_log_one(
            object: {
                corporate_id: $corporate_id
                action: "api_key_revoked"
                new_value: $key_id_text
                changed_by_user_id: $changed_by_user_id
            }
        ) {
            id
        }
    }
`;
